import { hash } from "daedalus-core";
import { AUTHORING_CELL, SIXTEENTHS } from "../movement/step.ts";
import { textDigest } from "../rules/version.ts";
import type { Rules } from "../rules/types.ts";

/**
 * Ground items (ADR-091's amendment for P2.8-T13, decision 3; P2.8-T63): a line a full bag could not take lies where
 * the creature fell, its owner's alone for `GROUND_OWNER_TICKS` and anyone's for `GROUND_PUBLIC_TICKS` more, both counted
 * in the ticks the item has lived, so a timer runs only while its map server ticks. The snapshot carries one as entity
 * kind 10 (`EntityKind.GroundItem`), its appearance the item's place in the rules' items plus 1 (`groundAppearance`),
 * which the page reads by the same rules, and its animation bits `GroundState`. A kill's lines are spread inside the
 * collision cell of the fall point by the shared seeded hash (`groundSpot`), so a page draws them apart and every
 * process puts them in the same place.
 */

/**
 * The ticks a ground item is its owner's alone: 5 minutes at 20 ticks a second.
 */
export const GROUND_OWNER_TICKS = 6000;

/**
 * The ticks a ground item is anyone's after its owner's time, before it goes: 5 minutes.
 */
export const GROUND_PUBLIC_TICKS = 6000;

/**
 * The most ground items one character owns at once; a character placing one more turns its oldest owned public.
 */
export const GROUND_OWNED_MAX = 20;

/**
 * A ground item's state on the wire, in the record's animation bits: owned (reserved for a character, whoever), public, or
 * owned by you (P2.8-T81, PROTOCOL 6.60.0): the state the map server sends only to the viewer whose character owns the
 * item, only while it is owned, so a page knows its own reserved items after a reload or a rejoin and learns nothing of
 * anyone else's. The server never reads it from a page. The Infamy status byte is not used for this (ADR-112 gives its
 * bits 2 to 5 to the staff badge).
 */
export const GroundState = { Owned: 0, Public: 1, OwnedByYou: 2 } as const;
export type GroundState = (typeof GroundState)[keyof typeof GroundState];

/**
 * A ground item's origin on the wire, in the record's Level byte (P2.8-T77, PROTOCOL 6.57.0), which a thing otherwise
 * leaves 0: `World` for an item the world put down as the snapshot spawns it (a creature's kill), so a page that sees it
 * land may play its rarity's sound; `Unknown` for everything else, an item a player caused, a row restored at a mount,
 * an item given its entity late and a server from before the byte, all of which a page keeps silent. The byte is an
 * enum: 2 to 255 are reserved for later origins (a chest's spill, a boss), which today's pages keep silent; an item level
 * or quality for a ground item needs a component of its own, or a split of this byte agreed in a new PROTOCOL entry.
 */
export const GroundOrigin = { Unknown: 0, World: 1 } as const;
export type GroundOrigin = (typeof GroundOrigin)[keyof typeof GroundOrigin];

/**
 * A ground item's quantity on the wire (P2.8-T85, PROTOCOL 6.59.0), in two bytes its record already has and a thing
 * otherwise leaves 0: the Health byte holds the low 8 bits and the PvP byte the high, so 1 to 999 fit with no new byte.
 *
 * @param quantity The units, 1 to 999.
 * @returns The Health and PvP bytes.
 * @example
 * groundQuantityFields(300);
 * // { health: 44, pvp: 1 }
 */
export function groundQuantityFields(quantity: number): { readonly health: number; readonly pvp: number } {
  return { health: quantity & 0xff, pvp: (quantity >>> 8) & 0xff };
}

/**
 * A ground item's quantity from its record's Health and PvP bytes; a record from a server before 6.59.0 carries 0 in
 * both, which reads as 1, the count a page showed before.
 *
 * @param state The record's Health and PvP bytes.
 * @returns The units, at least 1.
 * @example
 * groundQuantityOf({ health: 4, pvp: 0 });
 * // 4
 */
export function groundQuantityOf(state: { readonly health: number; readonly pvp: number }): number {
  return Math.max(1, state.health + state.pvp * 256);
}

/**
 * Whether a ground item's record says it is reserved for the page that reads it: its state is `GroundState.OwnedByYou`. A
 * public item, an item reserved for another character (state 0), a record from a server before 6.60.0 and a state a page
 * does not know all read false.
 *
 * @param state The record's state.
 * @returns True when the item is the viewer's own and still owned.
 * @example
 * groundYours({ anim: GroundState.OwnedByYou });
 * // true
 */
export function groundYours(state: { readonly anim: number }): boolean {
  return state.anim === GroundState.OwnedByYou;
}

/**
 * The sources a ground item's `source` record names that are the world's, not a player's (the author, 2026-10-10:
 * "No player induced drop will play the sound."): a creature's kill. A source not listed, a player's drop among them,
 * is never the world's, so a new source sounds only once it is added here on purpose.
 */
export const WORLD_GROUND_SOURCES: readonly string[] = Object.freeze(["creature"]);

/**
 * A ground item's origin from its `source` record.
 *
 * @param source The record a placement carries, its `source` naming what put the item down.
 * @returns `GroundOrigin.World` for a listed source, `GroundOrigin.Unknown` for any other.
 * @example
 * groundOriginOf({ source: "creature", line: 0 });
 * // GroundOrigin.World
 */
export function groundOriginOf(source: Readonly<Record<string, unknown>>): GroundOrigin {
  const named = source["source"];
  return typeof named === "string" && WORLD_GROUND_SOURCES.includes(named) ? GroundOrigin.World : GroundOrigin.Unknown;
}

/**
 * The seed of a kill's spread on the ground, apart from the roll's.
 */
export const GROUND_SPREAD_SEED = 0x47524e44;

/**
 * Which kill a line on the ground is of, as the roll keys it: the world, the map, the spawn definition, the creature's
 * slot, the tick of the fall and the recipient (its character's id, or `dev:<session>` for a dev join).
 */
export interface GroundSpotKey {
  readonly world: string;
  readonly map: string;
  readonly definition: string;
  readonly slot: number;
  readonly tick: number;
  readonly character: string;
}

/**
 * The size of an authoring collision cell in sixteenths of a tile: the spread and the relocation rings keep their reach whatever cell the compiled grid uses.
 */
const CELL = AUTHORING_CELL;

/**
 * The hash's input that tells the x offset from the y offset.
 */
const AXIS_X = 0;
const AXIS_Y = 1;

/**
 * A ground item's appearance: the item's place in the rules' items counting from 1, as the server's item numbers
 * count, so a page reads the item by the rules version it shares with the server.
 *
 * @param rules The rules.
 * @param item The item's rules id.
 * @returns The appearance, or 0 for an id the rules lack.
 * @example
 * groundAppearance(rules, "minor_healing_potion");
 * // 1, the first item of the shipped rules
 */
export function groundAppearance(rules: Rules, item: string): number {
  return rules.tables.items.findIndex((each) => each.id === item) + 1;
}

/**
 * Where one line of a kill lies: inside the collision cell holding the fall point, offset from the cell's corner by
 * the shared seeded hash over the kill's key and the line, the same on every call and every process.
 *
 * @param x The fall point's x, in sixteenths.
 * @param y The fall point's y, in sixteenths.
 * @param key The kill's key.
 * @param line The line's place in its loot table.
 * @returns The point, in sixteenths.
 * @throws RangeError when the slot, the tick or the line is not a whole number from 0 to 2^32 - 1.
 * @example
 * groundSpot(517, 1023, { world: "w", map: "m", definition: "e1", slot: 0, tick: 40, character: "dev:a" }, 2);
 * // a point in the cell of (517, 1023)
 */
export function groundSpot(x: number, y: number, key: GroundSpotKey, line: number): { readonly x: number; readonly y: number } {
  const world = textDigest(key.world, GROUND_SPREAD_SEED);
  const map = textDigest(key.map, GROUND_SPREAD_SEED);
  const definition = textDigest(key.definition, GROUND_SPREAD_SEED);
  const character = textDigest(key.character, GROUND_SPREAD_SEED);
  const dx = hash(GROUND_SPREAD_SEED, world, map, definition, key.slot, key.tick, character, line, AXIS_X) % CELL;
  const dy = hash(GROUND_SPREAD_SEED, world, map, definition, key.slot, key.tick, character, line, AXIS_Y) % CELL;
  return { x: x - (x % CELL) + dx, y: y - (y % CELL) + dy };
}

/**
 * How far a restored ground item looks for a place it stands on when the mounted version no longer stands on its own,
 * in tiles (P2.8-T66).
 */
export const GROUND_RELOCATE_TILES = 8;

/**
 * How long an ended ground item's row is kept for a ticket before the purge removes it, in days (P2.8-T66). An open
 * row is never purged.
 */
export const GROUND_HISTORY_DAYS = 90;

/**
 * Where a restored ground item stands (P2.8-T66): its own place when the mounted version stands on it, else the nearest
 * collision cell within `GROUND_RELOCATE_TILES` that it does, searched in square rings outward and, within a ring, along
 * the top edge left to right, the right edge top to bottom, the bottom edge right to left and the left edge bottom to
 * top, the item keeping its offset inside the cell; else the map's arrival point, which every map has.
 *
 * @param x The item's x, in sixteenths.
 * @param y The item's y, in sixteenths.
 * @param stands Whether an item may lie at a point: a creature could stand there.
 * @param arrival The map's arrival point.
 * @returns The point and whether it moved.
 * @example
 * relocateGround(100, 100, (px) => px >= 112, { x: 0, y: 0 });
 * // { x: 112, y: 88, moved: true }
 */
export function relocateGround(x: number, y: number, stands: (x: number, y: number) => boolean, arrival: { readonly x: number; readonly y: number }): { readonly x: number; readonly y: number; readonly moved: boolean } {
  if (stands(x, y)) {
    return { x, y, moved: false };
  }
  const rings = (GROUND_RELOCATE_TILES * SIXTEENTHS) / CELL;
  for (let r = 1; r <= rings; r += 1) {
    for (let side = 0; side < 4; side += 1) {
      for (let step = 0; step < 2 * r; step += 1) {
        const dx = side === 0 ? -r + step : side === 1 ? r : side === 2 ? r - step : -r;
        const dy = side === 0 ? -r : side === 1 ? -r + step : side === 2 ? r : r - step;
        const px = x + dx * CELL;
        const py = y + dy * CELL;
        if (px >= 0 && py >= 0 && stands(px, py)) {
          return { x: px, y: py, moved: true };
        }
      }
    }
  }
  return { x: arrival.x, y: arrival.y, moved: true };
}
